import React, { useState, useCallback, useRef } from 'react';
import PokemonSearchBar from './PokemonSearchBar';
import PokemonAdvancedSearch from './PokemonAdvancedSearch';
import PokemonCardGrid from './PokemonCardGrid';
import PokemonCardDetail from './PokemonCardDetail';

function PokemonApp({ apiUrl }) {
  const [searchMode, setSearchMode] = useState('simple');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  
  const currentSearchRef = useRef({ query: null, criteria: null, from: 0, pokedex: null });
  const searchIdRef = useRef(0);

  const handleSimpleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setResults(null);
      currentSearchRef.current = { query: null, criteria: null, from: 0, pokedex: null };
      return;
    }

    const searchId = ++searchIdRef.current;
    setLoading(true);
    setError(null);
    currentSearchRef.current = { query, criteria: null, from: 0, pokedex: null };

    try {
      const response = await fetch(
        `${apiUrl}/api/pokemon/search?q=${encodeURIComponent(query)}&size=20&from=0`
      );
      
      if (searchIdRef.current !== searchId) return;
      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults(data);
    } catch (err) {
      if (searchIdRef.current === searchId) {
        setError('Failed to search. Please try again.');
        console.error('Search error:', err);
      }
    } finally {
      if (searchIdRef.current === searchId) setLoading(false);
    }
  }, [apiUrl]);

  const handleAdvancedSearch = useCallback(async (criteria) => {
    const searchId = ++searchIdRef.current;
    setLoading(true);
    setError(null);
    currentSearchRef.current = { query: null, criteria, from: 0, pokedex: null };

    try {
      const response = await fetch(`${apiUrl}/api/pokemon/search/advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ criteria, size: 20, from: 0 }),
      });

      if (searchIdRef.current !== searchId) return;
      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults(data);
    } catch (err) {
      if (searchIdRef.current === searchId) {
        setError('Failed to search. Please try again.');
        console.error('Search error:', err);
      }
    } finally {
      if (searchIdRef.current === searchId) setLoading(false);
    }
  }, [apiUrl]);

  const handlePokedexSearch = useCallback(async (pokedexNumber) => {
    const searchId = ++searchIdRef.current;
    setLoading(true);
    setError(null);
    setSelectedCard(null); // Close the card detail
    currentSearchRef.current = { query: null, criteria: null, from: 0, pokedex: pokedexNumber };

    try {
      const response = await fetch(
        `${apiUrl}/api/pokemon/search/pokedex/${pokedexNumber}?size=100&from=0`
      );
      
      if (searchIdRef.current !== searchId) return;
      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults({
        ...data,
        total: data.total,
        cards: data.cards,
        query: `Pokédex #${pokedexNumber}`
      });
    } catch (err) {
      if (searchIdRef.current === searchId) {
        setError('Failed to search. Please try again.');
        console.error('Pokedex search error:', err);
      }
    } finally {
      if (searchIdRef.current === searchId) setLoading(false);
    }
  }, [apiUrl]);

  const handleArtistSearch = useCallback(async (artist) => {
    const searchId = ++searchIdRef.current;
    setLoading(true);
    setError(null);
    setSelectedCard(null);
    // Use exact matching for artist clicks
    const criteria = { artist: { value: artist, exact: true } };
    currentSearchRef.current = { query: null, criteria, from: 0, pokedex: null };

    try {
      const response = await fetch(`${apiUrl}/api/pokemon/search/advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ criteria, size: 20, from: 0 }),
      });

      if (searchIdRef.current !== searchId) return;
      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults({
        ...data,
        query: `Artist: ${artist}`
      });
    } catch (err) {
      if (searchIdRef.current === searchId) {
        setError('Failed to search. Please try again.');
        console.error('Artist search error:', err);
      }
    } finally {
      if (searchIdRef.current === searchId) setLoading(false);
    }
  }, [apiUrl]);

  const handleSetSearch = useCallback(async (setName) => {
    const searchId = ++searchIdRef.current;
    setLoading(true);
    setError(null);
    setSelectedCard(null);
    const criteria = { set: { value: setName } };
    currentSearchRef.current = { query: null, criteria, from: 0, pokedex: null };

    try {
      const response = await fetch(`${apiUrl}/api/pokemon/search/advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ criteria, size: 20, from: 0 }),
      });

      if (searchIdRef.current !== searchId) return;
      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults({
        ...data,
        query: `Set: ${setName}`
      });
    } catch (err) {
      if (searchIdRef.current === searchId) {
        setError('Failed to search. Please try again.');
        console.error('Set search error:', err);
      }
    } finally {
      if (searchIdRef.current === searchId) setLoading(false);
    }
  }, [apiUrl]);

  const loadMore = useCallback(async () => {
    if (!results || loading) return;

    const currentSearch = currentSearchRef.current;
    const newFrom = currentSearch.from + 20;
    if (newFrom >= results.total) return;

    const searchId = searchIdRef.current;
    setLoading(true);

    try {
      let response;
      
      if (currentSearch.pokedex) {
        response = await fetch(
          `${apiUrl}/api/pokemon/search/pokedex/${currentSearch.pokedex}?size=20&from=${newFrom}`
        );
      } else if (currentSearch.query) {
        response = await fetch(
          `${apiUrl}/api/pokemon/search?q=${encodeURIComponent(currentSearch.query)}&size=20&from=${newFrom}`
        );
      } else if (currentSearch.criteria) {
        response = await fetch(`${apiUrl}/api/pokemon/search/advanced`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ criteria: currentSearch.criteria, size: 20, from: newFrom }),
        });
      } else {
        setLoading(false);
        return;
      }

      if (searchIdRef.current !== searchId) return;
      if (!response.ok) throw new Error('Failed to load more');

      const data = await response.json();
      currentSearchRef.current.from = newFrom;
      
      setResults(prev => ({
        ...prev,
        cards: [...prev.cards, ...data.cards],
      }));
    } catch (err) {
      if (searchIdRef.current === searchId) setError('Failed to load more results.');
    } finally {
      if (searchIdRef.current === searchId) setLoading(false);
    }
  }, [results, loading, apiUrl]);

  const handleCardClick = useCallback((card) => setSelectedCard(card), []);
  const handleCloseDetail = useCallback(() => setSelectedCard(null), []);

  return (
    <>
      <div className="search-container">
        <div className="search-mode-toggle">
          <button
            className={`mode-btn ${searchMode === 'simple' ? 'active' : ''}`}
            onClick={() => setSearchMode('simple')}
          >
            Quick Search
          </button>
          <button
            className={`mode-btn ${searchMode === 'advanced' ? 'active' : ''}`}
            onClick={() => setSearchMode('advanced')}
          >
            Advanced Search
          </button>
        </div>

        {searchMode === 'simple' ? (
          <PokemonSearchBar onSearch={handleSimpleSearch} apiUrl={apiUrl} />
        ) : (
          <PokemonAdvancedSearch onSearch={handleAdvancedSearch} apiUrl={apiUrl} />
        )}
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠</span>
          {error}
        </div>
      )}

      {results && (
        <div className="results-container">
          <div className="results-header">
            <span className="results-count">
              {results.total.toLocaleString()} card{results.total !== 1 ? 's' : ''} found
              {results.query && ` for "${results.query}"`}
            </span>
            <span className="results-time">in {results.took}ms</span>
          </div>

          <PokemonCardGrid
            cards={results.cards}
            onCardClick={handleCardClick}
            apiUrl={apiUrl}
          />

          {results.cards.length < results.total && (
            <div className="load-more-container">
              <button className="load-more-btn" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}

      {loading && !results && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Searching the Pokédex...</p>
        </div>
      )}

      {!results && !loading && (
        <div className="welcome-message">
          <div className="welcome-icon">✦</div>
          <h2>Begin Your Search</h2>
          <p>Search for Pokémon cards by name or use advanced search to filter by type, HP, set, and more.</p>
        </div>
      )}

      {selectedCard && (
        <PokemonCardDetail
          card={selectedCard}
          onClose={handleCloseDetail}
          apiUrl={apiUrl}
          onPokedexClick={handlePokedexSearch}
          onArtistClick={handleArtistSearch}
          onSetClick={handleSetSearch}
        />
      )}
    </>
  );
}

export default PokemonApp;
